import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import {
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  CircleDollarSign,
  Clock3,
  FileSignature,
  PercentCircle,
  Plus,
  Search,
  Send,
  ShieldCheck,
  UserRound,
  XCircle
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import {
  FREQUENCY_LABELS,
  LEVEL_GROUPS,
  SCHOOL_YEAR,
  SERVICE_CATALOG,
  detectLevelGroup,
  installmentsCount,
  tariffTotal
} from './pricingCatalog';

const money = (value) => `${Number(value || 0).toFixed(0)} DT`;
const text = (value) => String(value ?? '').trim();
const fullName = (item) => text(item?.fullName || item?.fullname || item?.name) || 'Sans nom';
const isoDate = (year, month, day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;


const dateValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

function trackingMeta(contract) {
  if (!contract) {
    return {
      key: 'missing',
      label: 'Contrat non créé',
      tone: 'missing',
      action: 'Créer le contrat',
      Icon: CircleAlert
    };
  }

  if (contract.status === 'accepted' || contract.accepted === true) {
    return {
      key: 'accepted',
      label: 'Accepté',
      tone: 'accepted',
      action: 'Voir le contrat',
      Icon: CircleCheckBig
    };
  }

  if (
    contract.status === 'sent_to_parent' ||
    contract.status === 'pending_parent_acceptance'
  ) {
    return {
      key: 'waiting_parent',
      label: 'En attente du parent',
      tone: 'waiting',
      action: 'Consulter',
      Icon: Clock3
    };
  }

  if (
    contract.status === 'pending_manager_approval' ||
    contract.discountStatus === 'pending'
  ) {
    return {
      key: 'manager_approval',
      label: 'Validation gérante',
      tone: 'approval',
      action: 'Examiner',
      Icon: ShieldCheck
    };
  }

  if (contract.status === 'discount_rejected') {
    return {
      key: 'rejected',
      label: 'Remise refusée',
      tone: 'rejected',
      action: 'Corriger',
      Icon: CircleAlert
    };
  }

  return {
    key: 'draft',
    label: 'Brouillon',
    tone: 'draft',
    action: 'Continuer',
    Icon: FileSignature
  };
}

function dueDates(frequency, year = 2026) {
  if (frequency === 'monthly') {
    return [9,10,11,12,1,2,3,4,5,6].map((month, index) => isoDate(month >= 9 ? year : year + 1, month, 1));
  }
  if (frequency === 'quarterly') return [isoDate(year, 9, 15), isoDate(year, 12, 15), isoDate(year + 1, 3, 15)];
  return [isoDate(year, 9, 15)];
}

function allocateDiscount(amount, count) {
  const total = Math.max(0, Number(amount || 0));
  if (!total || !count) return Array(count).fill(0);
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / count);
  const remainder = cents - base * count;
  return Array.from({ length: count }, (_, index) => (base + (index < remainder ? 1 : 0)) / 100);
}

function createSchedule({ registrationAmount, tuitionFrequency, tuitionUnitAmount, tuitionDiscountAmount, services, otherFees }) {
  const schedule = [];
  if (registrationAmount > 0) {
    schedule.push({
      id: `registration-${Date.now()}`,
      category: 'registration',
      label: 'Frais d’inscription',
      dueDate: isoDate(2026, 9, 15),
      amount: Number(registrationAmount),
      paidAmount: 0,
      remainingAmount: Number(registrationAmount),
      status: 'upcoming'
    });
  }

  const tuitionDates = dueDates(tuitionFrequency);
  const discountParts = allocateDiscount(tuitionDiscountAmount, tuitionDates.length);
  tuitionDates.forEach((dueDate, index) => {
    const raw = Number(tuitionUnitAmount || 0);
    const finalAmount = Math.max(0, raw - discountParts[index]);
    schedule.push({
      id: `tuition-${index + 1}-${Date.now()}`,
      category: 'tuition',
      label: tuitionFrequency === 'monthly' ? `Scolarité mois ${index + 1}` : tuitionFrequency === 'quarterly' ? `Scolarité trimestre ${index + 1}` : 'Scolarité annuelle',
      dueDate,
      standardAmount: raw,
      discountAmount: discountParts[index],
      amount: finalAmount,
      paidAmount: 0,
      remainingAmount: finalAmount,
      status: 'upcoming'
    });
  });

  services.filter((service) => service.enabled).forEach((service) => {
    const dates = service.frequency === 'daily' ? [service.dueDate || isoDate(2026, 9, 15)] : dueDates(service.frequency);
    dates.forEach((dueDate, index) => {
      const amount = service.frequency === 'daily'
        ? Number(service.unitAmount || 0) * Number(service.quantity || 0)
        : Number(service.unitAmount || 0);
      schedule.push({
        id: `${service.id}-${index + 1}-${Date.now()}`,
        category: service.id,
        label: service.frequency === 'monthly' ? `${service.label} mois ${index + 1}` : service.frequency === 'quarterly' ? `${service.label} trimestre ${index + 1}` : service.label,
        dueDate,
        amount,
        paidAmount: 0,
        remainingAmount: amount,
        status: 'upcoming'
      });
    });
  });

  otherFees.filter((fee) => fee.label && Number(fee.amount) > 0).forEach((fee, index) => {
    schedule.push({
      id: `other-${index + 1}-${Date.now()}`,
      category: 'other',
      label: fee.label,
      dueDate: fee.dueDate || isoDate(2026, 9, 15),
      amount: Number(fee.amount),
      paidAmount: 0,
      remainingAmount: Number(fee.amount),
      status: fee.optional ? 'pending_confirmation' : 'upcoming',
      optional: Boolean(fee.optional)
    });
  });

  return schedule.sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
}

export default function ContractsPage() {
  const { user, profile, isGerant } = useAuth();
  const contractBuilderRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(searchParams.get('parentId') || '');
  const [selectedStudentId, setSelectedStudentId] = useState(searchParams.get('studentId') || '');
  const [search, setSearch] = useState('');
  const [trackingFilter, setTrackingFilter] = useState('all');
  const [selectedContractId, setSelectedContractId] = useState(searchParams.get('contractId') || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tuitionFrequency, setTuitionFrequency] = useState('monthly');
  const [levelGroupId, setLevelGroupId] = useState('preschool');
  const [discountReason, setDiscountReason] = useState('none');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountNote, setDiscountNote] = useState('');
  const [services, setServices] = useState(() => Object.values(SERVICE_CATALOG).map((service) => ({
    ...service,
    enabled: false,
    frequency: Object.keys(service.frequencies)[0],
    unitAmount: Object.values(service.frequencies)[0],
    quantity: service.id === 'daycare' ? 1 : 1,
    dueDate: isoDate(2026, 9, 15)
  })));
  const [otherFees, setOtherFees] = useState([]);

  useEffect(() => {
    const offParents = onSnapshot(collection(db, 'parents'), (snapshot) => setParents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const offStudents = onSnapshot(collection(db, 'students'), (snapshot) => setStudents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const offContracts = onSnapshot(collection(db, 'contracts'), (snapshot) => setContracts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { offParents(); offStudents(); offContracts(); };
  }, []);

  const selectedParent = parents.find((item) => item.id === selectedParentId) || null;
  const linkedStudents = students.filter(
    (student) =>
      student.primaryParentId === selectedParentId ||
      student.parentId === selectedParentId ||
      (Array.isArray(student.parentIds) &&
        student.parentIds.includes(selectedParentId))
  );
  const selectedStudent = students.find((item) => item.id === selectedStudentId) || null;
  const selectedContract = contracts.find((item) => item.id === selectedContractId) || null;

  useEffect(() => {
    if (!selectedStudent) return;
    setLevelGroupId(detectLevelGroup(selectedStudent.level || selectedStudent.requestedLevel || selectedStudent.classLabel));
  }, [selectedStudentId]);

  useEffect(() => {
    if (!selectedParentId) setSelectedStudentId('');
    else if (linkedStudents.length && !linkedStudents.some((student) => student.id === selectedStudentId)) setSelectedStudentId(linkedStudents[0].id);
  }, [selectedParentId, students.length]);


  useEffect(() => {
    const contractId = searchParams.get('contractId');
    if (
      contractId &&
      contracts.some((contract) => contract.id === contractId)
    ) {
      setSelectedContractId(contractId);
    }
  }, [contracts, searchParams]);

  const group = LEVEL_GROUPS[levelGroupId];
  const tuitionUnitAmount = Number(group.tuition[tuitionFrequency]);
  const tuitionBaseTotal = tariffTotal(tuitionUnitAmount, tuitionFrequency);
  const effectiveDiscount = Math.min(Math.max(0, Number(discountAmount || 0)), tuitionBaseTotal);
  const tuitionFinalTotal = Math.max(0, tuitionBaseTotal - effectiveDiscount);
  const registrationAmount = Number(group.registration);

  useEffect(() => {
    if (discountReason === 'staff_child') setDiscountAmount(Math.round(tuitionBaseTotal / 2));
    if (discountReason === 'none') setDiscountAmount(0);
  }, [discountReason, tuitionBaseTotal]);

  const serviceTotal = useMemo(() => services.reduce((sum, service) => {
    if (!service.enabled) return sum;
    return sum + tariffTotal(service.unitAmount, service.frequency, service.quantity);
  }, 0), [services]);
  const otherTotal = useMemo(() => otherFees.reduce((sum, fee) => sum + Number(fee.amount || 0), 0), [otherFees]);
  const totalAmount = registrationAmount + tuitionFinalTotal + serviceTotal + otherTotal;
  const schedule = useMemo(() => createSchedule({
    registrationAmount,
    tuitionFrequency,
    tuitionUnitAmount,
    tuitionDiscountAmount: effectiveDiscount,
    services,
    otherFees
  }), [registrationAmount, tuitionFrequency, tuitionUnitAmount, effectiveDiscount, services, otherFees]);

  const latestContractByStudent = useMemo(() => {
    const map = new Map();

    contracts
      .filter(
        (contract) =>
          !contract.schoolYear || contract.schoolYear === SCHOOL_YEAR
      )
      .sort(
        (a, b) =>
          Math.max(dateValue(b.updatedAt), dateValue(b.createdAt)) -
          Math.max(dateValue(a.updatedAt), dateValue(a.createdAt))
      )
      .forEach((contract) => {
        if (contract.studentId && !map.has(contract.studentId)) {
          map.set(contract.studentId, contract);
        }
      });

    return map;
  }, [contracts]);

  const trackingRows = useMemo(
    () =>
      students.map((student) => {
        const contract = latestContractByStudent.get(student.id) || null;
        const linkedParentId =
          student.primaryParentId ||
          student.parentId ||
          student.parentIds?.[0] ||
          contract?.parentId;

        const parent =
          parents.find((item) => item.id === linkedParentId) || null;

        return {
          student,
          parent,
          contract,
          meta: trackingMeta(contract)
        };
      }),
    [students, parents, latestContractByStudent]
  );

  const trackingCounts = useMemo(() => {
    const counts = {
      all: trackingRows.length,
      incomplete: 0,
      missing: 0,
      draft: 0,
      manager_approval: 0,
      waiting_parent: 0,
      accepted: 0
    };

    trackingRows.forEach(({ meta }) => {
      if (counts[meta.key] !== undefined) {
        counts[meta.key] += 1;
      }
      if (meta.key !== 'accepted') {
        counts.incomplete += 1;
      }
    });

    return counts;
  }, [trackingRows]);

  const visibleTrackingRows = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return trackingRows.filter(({ student, parent, contract, meta }) => {
      const matchesSearch =
        !needle ||
        [
          fullName(student),
          fullName(parent),
          student.level,
          student.requestedLevel,
          student.classLabel,
          contract?.schoolYear,
          meta.label
        ].some((value) =>
          text(value).toLowerCase().includes(needle)
        );

      const matchesFilter =
        trackingFilter === 'all' ||
        (trackingFilter === 'incomplete' &&
          meta.key !== 'accepted') ||
        trackingFilter === meta.key;

      return matchesSearch && matchesFilter;
    });
  }, [trackingRows, trackingFilter, search]);

  function openTrackingRow(row) {
    const parentId =
      row.parent?.id ||
      row.contract?.parentId ||
      row.student.primaryParentId ||
      row.student.parentId ||
      row.student.parentIds?.[0] ||
      '';

    if (!parentId) {
      setError(
        'Impossible d’ouvrir la création du contrat : aucun parent n’est lié à cet enfant.'
      );
      return;
    }

    setError('');
    setSelectedParentId(parentId);
    setSelectedStudentId(row.student.id);

    const nextParams = new URLSearchParams({
      parentId,
      studentId: row.student.id
    });

    if (row.contract?.id) {
      setSelectedContractId(row.contract.id);
      nextParams.set('contractId', row.contract.id);
    } else {
      setSelectedContractId('');
    }

    setSearchParams(nextParams);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        contractBuilderRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });
  }

  function updateService(id, patch) {
    setServices((current) => current.map((service) => {
      if (service.id !== id) return service;
      const next = { ...service, ...patch };
      if (patch.frequency) next.unitAmount = service.frequencies[patch.frequency];
      return next;
    }));
  }

  function addOtherFee() {
    setOtherFees((current) => [...current, { id: crypto.randomUUID(), label: '', amount: '', dueDate: isoDate(2026, 9, 15), optional: false }]);
  }

  async function saveContract(sendToParent = false) {
    if (!selectedParent || !selectedStudent) return setError('Choisissez un parent et un enfant.');
    setSaving(true);
    setError('');
    try {
      const hasDiscount = effectiveDiscount > 0;
     const status = hasDiscount && !isGerant
  ? 'pending_manager_approval'
  : sendToParent
    ? 'active'
    : 'draft';
      const items = [
        { type: 'registration', label: 'Inscription', standardPrice: registrationAmount, discountAmount: 0, finalPrice: registrationAmount, frequency: 'once' },
        { type: 'tuition', label: 'Scolarité', standardPrice: tuitionBaseTotal, unitPrice: tuitionUnitAmount, discountAmount: effectiveDiscount, finalPrice: tuitionFinalTotal, frequency: tuitionFrequency }
      ];
      services.filter((service) => service.enabled).forEach((service) => items.push({
        type: service.id,
        label: service.label,
        standardPrice: tariffTotal(service.unitAmount, service.frequency, service.quantity),
        unitPrice: Number(service.unitAmount),
        quantity: Number(service.quantity || 1),
        discountAmount: 0,
        finalPrice: tariffTotal(service.unitAmount, service.frequency, service.quantity),
        frequency: service.frequency
      }));
      otherFees.filter((fee) => fee.label && Number(fee.amount) > 0).forEach((fee) => items.push({
        type: 'other', label: fee.label, standardPrice: Number(fee.amount), discountAmount: 0, finalPrice: Number(fee.amount), frequency: 'once', optional: Boolean(fee.optional), dueDate: fee.dueDate
      }));

      const payload = {
        parentId: selectedParent.id,
        parentName: fullName(selectedParent),
        studentId: selectedStudent.id,
        studentName: fullName(selectedStudent),
        studentLevel: selectedStudent.level || selectedStudent.requestedLevel || selectedStudent.classLabel || group.label,
        levelGroupId,
        levelGroupLabel: group.label,
        schoolYear: SCHOOL_YEAR,
        status,
        items,
        installments: schedule,
        registrationAmount,
        tuitionFrequency,
        tuitionUnitAmount,
        tuitionBaseTotal,
        discountAmount: effectiveDiscount,
        discountReason,
        discountNote: text(discountNote),
        discountStatus: hasDiscount ? (isGerant ? 'approved' : 'pending') : 'not_applicable',
        discountRequestedByUid: hasDiscount ? profile?.id || null : null,
        discountRequestedByName: hasDiscount ? fullName(profile) : null,
        discountRequestedAt: hasDiscount ? serverTimestamp() : null,
        discountApprovedByUid: hasDiscount && isGerant ? profile?.id || null : null,
        discountApprovedAt: hasDiscount && isGerant ? serverTimestamp() : null,
        tuitionFinalTotal,
        servicesTotal: serviceTotal,
        otherFeesTotal: otherTotal,
        totalAmount,
        paidAmount: 0,
        remainingAmount: totalAmount,
        active: status === 'active',
        activatedAt: status === 'active' ? serverTimestamp() : null,
        activatedByUid: status === 'active' ? profile?.id || null : null,
        activatedByName: status === 'active' ? fullName(profile) : null,
        sentAt: sendToParent && (!hasDiscount || isGerant) ? serverTimestamp() : null,
        createdByUid: user?.uid || null,
        createdByName: fullName(profile),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
     console.log(
  'CONTRACT DEBUG VALUES:\n' +
  JSON.stringify(
    {
      authUid: user?.uid,
      createdByUid: payload.createdByUid,
      status: payload.status,
      active: payload.active,
      discountAmount: payload.discountAmount,
      discountStatus: payload.discountStatus,
      role: profile?.role,
      adminRole: profile?.adminRole,
      adminRoles: profile?.adminRoles,
      profileStatus: profile?.status
    },
    null,
    2
  )
);
      const created = await addDoc(collection(db, 'contracts'), payload);
      setSelectedContractId(created.id);
      setSearchParams({
        parentId: selectedParent.id,
        studentId: selectedStudent.id,
        contractId: created.id
      });
      setMessage(status === 'pending_manager_approval' ? 'Contrat enregistré et envoyé à la gérante pour validation.' : sendToParent ? 'Contrat envoyé au parent.' : 'Contrat enregistré en brouillon.');
    } catch (err) {
      console.error(err);
      setError(`Impossible d’enregistrer le contrat : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function approveContract(contract, approved) {
  if (!isGerant) return;

  await updateDoc(doc(db, 'contracts', contract.id), {
    status: approved ? 'active' : 'discount_rejected',

    active: approved,

    activatedAt: approved
      ? serverTimestamp()
      : null,

    activatedByUid: approved
      ? profile?.id || null
      : null,

    activatedByName: approved
      ? fullName(profile)
      : null,

    discountStatus: approved
      ? 'approved'
      : 'rejected',

    discountApprovedByUid: profile?.id || null,
    discountApprovedByName: fullName(profile),
    discountApprovedAt: serverTimestamp(),

    updatedAt: serverTimestamp()
  });

  setMessage(
    approved
      ? 'Remise approuvée et contrat activé.'
      : 'Remise refusée.'
  );
}

  async function sendExistingContract(contract) {
  await updateDoc(doc(db, 'contracts', contract.id), {
    status: 'active',
    active: true,
    activatedAt: serverTimestamp(),
    activatedByUid: profile?.id || null,
    activatedByName: fullName(profile),
    sentAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  setMessage('Contrat finalisé et disponible dans l’espace parent.');
}

  return <>
    <PageHeader eyebrow="FINANCE V0" title="Contrats et échéanciers" description="Créez un contrat financier par enfant, appliquez les tarifs officiels et soumettez les remises à la gérante." />
    {error && <div className="alert alert-error">{error}</div>}
    {message && <div className="success-note"><CheckCircle2 size={17}/>{message}</div>}

    <section className="finance-v0-layout" ref={contractBuilderRef}>
      <div className="finance-v0-main">
        <section className="panel contract-builder-v0">
          <div className="panel-heading"><div><p className="eyebrow">NOUVEAU CONTRAT</p><h2>Contrat par enfant</h2></div><span className="live-badge"><FileSignature size={15}/>Année {SCHOOL_YEAR}</span></div>

          <div className="contract-step-v0">
            <h3><span>1</span> Parent et enfant</h3>
            <div className="form-two-columns">
              <label>Parent<select value={selectedParentId} onChange={(e) => setSelectedParentId(e.target.value)}><option value="">Choisir un parent</option>{parents.sort((a,b)=>fullName(a).localeCompare(fullName(b),'fr')).map((parent)=><option key={parent.id} value={parent.id}>{fullName(parent)}</option>)}</select></label>
              <label>Enfant<select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)} disabled={!selectedParentId}><option value="">Choisir un enfant</option>{linkedStudents.map((student)=><option key={student.id} value={student.id}>{fullName(student)} — {student.level || student.requestedLevel || 'Niveau à confirmer'}</option>)}</select></label>
            </div>
            <label>Groupe tarifaire<select value={levelGroupId} onChange={(e) => setLevelGroupId(e.target.value)}>{Object.values(LEVEL_GROUPS).map((item)=><option value={item.id} key={item.id}>{item.label}</option>)}</select></label>
          </div>

          <div className="contract-step-v0">
            <h3><span>2</span> Inscription et scolarité</h3>
            <div className="contract-price-line-v0"><div><strong>Inscription annuelle</strong><small>Une fois par année scolaire, sans remise</small></div><b>{money(registrationAmount)}</b></div>
            <div className="tuition-choice-grid-v0">{['monthly','quarterly','annual'].map((frequency)=><button type="button" className={tuitionFrequency===frequency?'selected':''} key={frequency} onClick={()=>setTuitionFrequency(frequency)}><span>{FREQUENCY_LABELS[frequency]}</span><strong>{money(group.tuition[frequency])}{frequency!=='annual'&&<small> / échéance</small>}</strong><small>{frequency==='monthly'?'10 échéances — tarif normal':frequency==='quarterly'?'3 échéances — tarif préférentiel':'1 échéance — tarif préférentiel'}</small></button>)}</div>
          </div>

          <div className="contract-step-v0">
            <h3><span>3</span> Remise exceptionnelle sur la scolarité</h3>
            <p className="privacy-note">La remise ne peut jamais concerner l’inscription, le transport, la cantine, le panier, la garderie ou les autres frais.</p>
            <div className="form-two-columns">
              <label>Motif<select value={discountReason} onChange={(e)=>setDiscountReason(e.target.value)}><option value="none">Aucune remise</option><option value="staff_child">Enfant du personnel — proposition 50 %</option><option value="family">Remise familiale</option><option value="social">Cas social exceptionnel</option><option value="commercial">Geste commercial</option><option value="other">Autre</option></select></label>
              <label>Montant de la remise<input type="number" min="0" max={tuitionBaseTotal} value={discountAmount} onChange={(e)=>setDiscountAmount(e.target.value)} disabled={discountReason==='none'}/></label>
            </div>
            {discountReason!=='none'&&<label>Motif détaillé<textarea value={discountNote} onChange={(e)=>setDiscountNote(e.target.value)} placeholder="Expliquez la remise demandée…"/></label>}
            {effectiveDiscount>0&&<div className="discount-approval-v0"><ShieldCheck/><div><strong>Validation de la gérante obligatoire</strong><span>Tarif scolarité : {money(tuitionBaseTotal)} − remise {money(effectiveDiscount)} = {money(tuitionFinalTotal)}</span></div></div>}
          </div>

          <div className="contract-step-v0">
            <h3><span>4</span> Services périscolaires</h3>
            <div className="service-grid-v0">{services.map((service)=><article className={`service-card-v0 ${service.enabled?'enabled':''}`} key={service.id}><label className="service-toggle-v0"><input type="checkbox" checked={service.enabled} onChange={(e)=>updateService(service.id,{enabled:e.target.checked})}/><div><strong>{service.label}</strong>{service.note&&<small>{service.note}</small>}</div></label>{service.enabled&&<><label>Formule<select value={service.frequency} onChange={(e)=>updateService(service.id,{frequency:e.target.value})}>{Object.keys(service.frequencies).map((frequency)=><option value={frequency} key={frequency}>{FREQUENCY_LABELS[frequency]} — {money(service.frequencies[frequency])}</option>)}</select></label>{service.frequency==='daily'&&<div className="form-two-columns"><label>Nombre de jours<input type="number" min="1" value={service.quantity} onChange={(e)=>updateService(service.id,{quantity:e.target.value})}/></label><label>Échéance<input type="date" value={service.dueDate} onChange={(e)=>updateService(service.id,{dueDate:e.target.value})}/></label></div>}<b>Total : {money(tariffTotal(service.unitAmount,service.frequency,service.quantity))}</b></>}</article>)}</div>
          </div>

          <div className="contract-step-v0">
            <div className="panel-heading"><h3><span>5</span> Autres frais pendant l’année</h3><button type="button" className="button button-secondary" onClick={addOtherFee}><Plus size={16}/>Ajouter un frais</button></div>
            {otherFees.map((fee,index)=><div className="other-fee-row-v0" key={fee.id}><input placeholder="Libellé : sortie, événement…" value={fee.label} onChange={(e)=>setOtherFees((current)=>current.map((item)=>item.id===fee.id?{...item,label:e.target.value}:item))}/><input type="number" min="0" placeholder="Montant" value={fee.amount} onChange={(e)=>setOtherFees((current)=>current.map((item)=>item.id===fee.id?{...item,amount:e.target.value}:item))}/><input type="date" value={fee.dueDate} onChange={(e)=>setOtherFees((current)=>current.map((item)=>item.id===fee.id?{...item,dueDate:e.target.value}:item))}/><label><input type="checkbox" checked={fee.optional} onChange={(e)=>setOtherFees((current)=>current.map((item)=>item.id===fee.id?{...item,optional:e.target.checked}:item))}/> Optionnel</label><button type="button" className="icon-button" onClick={()=>setOtherFees((current)=>current.filter((item)=>item.id!==fee.id))}><XCircle size={18}/></button></div>)}
            {!otherFees.length&&<div className="compact-empty">Aucun frais additionnel.</div>}
          </div>

          <div className="contract-actions-v0"><button className="button button-secondary" disabled={saving} onClick={()=>saveContract(false)}>Enregistrer en brouillon</button><button className="button button-primary" disabled={saving||!selectedStudent} onClick={()=>saveContract(true)}><Send size={16}/>{effectiveDiscount>0&&!isGerant?'Demander validation':'Envoyer au parent'}</button></div>
        </section>
      </div>

      <aside className="finance-v0-side">
        <section className="panel contract-summary-side-v0"><p className="eyebrow">RÉSUMÉ</p><h2>{selectedStudent?fullName(selectedStudent):'Nouveau contrat'}</h2><dl><div><dt>Inscription</dt><dd>{money(registrationAmount)}</dd></div><div><dt>Scolarité</dt><dd>{money(tuitionFinalTotal)}</dd></div><div><dt>Services</dt><dd>{money(serviceTotal)}</dd></div><div><dt>Autres frais</dt><dd>{money(otherTotal)}</dd></div><div className="discount"><dt>Remise scolarité</dt><dd>− {money(effectiveDiscount)}</dd></div><div className="total"><dt>Total contrat</dt><dd>{money(totalAmount)}</dd></div></dl><div className="schedule-preview-v0"><strong>{schedule.length} échéance{schedule.length>1?'s':''}</strong>{schedule.slice(0,5).map((item)=><div key={item.id}><span>{item.label}<small>{item.dueDate}</small></span><b>{money(item.amount)}</b></div>)}{schedule.length>5&&<small>+ {schedule.length-5} autres échéances</small>}</div></section>
      </aside>
    </section>

    <section className="contract-tracking-kpis">
      <button
        type="button"
        className={`contract-tracking-kpi ${trackingFilter === 'missing' ? 'active' : ''}`}
        onClick={() => setTrackingFilter('missing')}
      >
        <CircleAlert />
        <span>Contrats à créer</span>
        <strong>{trackingCounts.missing}</strong>
      </button>

      <button
        type="button"
        className={`contract-tracking-kpi ${trackingFilter === 'draft' ? 'active' : ''}`}
        onClick={() => setTrackingFilter('draft')}
      >
        <FileSignature />
        <span>Brouillons</span>
        <strong>{trackingCounts.draft}</strong>
      </button>

      <button
        type="button"
        className={`contract-tracking-kpi ${trackingFilter === 'waiting_parent' ? 'active' : ''}`}
        onClick={() => setTrackingFilter('waiting_parent')}
      >
        <Clock3 />
        <span>En attente du parent</span>
        <strong>{trackingCounts.waiting_parent}</strong>
      </button>

      <button
        type="button"
        className={`contract-tracking-kpi ${trackingFilter === 'accepted' ? 'active' : ''}`}
        onClick={() => setTrackingFilter('accepted')}
      >
        <CircleCheckBig />
        <span>Contrats acceptés</span>
        <strong>{trackingCounts.accepted}</strong>
      </button>
    </section>

    <section className="panel contract-tracking-panel">
      <div className="panel-heading contract-tracking-heading">
        <div>
          <p className="eyebrow">SUIVI GLOBAL</p>
          <h2>
            {trackingCounts.accepted}/{trackingCounts.all} enfant
            {trackingCounts.all > 1 ? 's' : ''} prêt
            {trackingCounts.accepted > 1 ? 's' : ''}
          </h2>
        </div>

        <div className="contract-tracking-tools">
          <label className="student-search">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Parent, enfant, niveau…"
            />
          </label>

          <select
            value={trackingFilter}
            onChange={(event) => setTrackingFilter(event.target.value)}
          >
            <option value="all">Tous les dossiers ({trackingCounts.all})</option>
            <option value="incomplete">Dossiers incomplets ({trackingCounts.incomplete})</option>
            <option value="missing">Contrats non créés ({trackingCounts.missing})</option>
            <option value="draft">Brouillons ({trackingCounts.draft})</option>
            <option value="manager_approval">Validation gérante ({trackingCounts.manager_approval})</option>
            <option value="waiting_parent">En attente du parent ({trackingCounts.waiting_parent})</option>
            <option value="accepted">Acceptés ({trackingCounts.accepted})</option>
          </select>
        </div>
      </div>

      <div className="contract-tracking-list">
        {visibleTrackingRows.length ? (
          visibleTrackingRows.map((row) => {
            const StatusIcon = row.meta.Icon;

            return (
              <button
                type="button"
                key={row.student.id}
                className="contract-tracking-row"
                onClick={() => openTrackingRow(row)}
              >
                <span className="student-avatar">
                  <FileSignature size={18} />
                </span>

                <span className="student-main">
                  <strong>{fullName(row.student)}</strong>
                  <small>
                    {fullName(row.parent)} ·{' '}
                    {row.student.level ||
                      row.student.requestedLevel ||
                      row.student.classLabel ||
                      'Niveau à confirmer'}
                  </small>
                </span>

                <span className={`contract-tracking-status ${row.meta.tone}`}>
                  <StatusIcon size={15} />
                  {row.meta.label}
                </span>

                <span className="contract-tracking-amount">
                  {row.contract ? money(row.contract.totalAmount) : '—'}
                </span>

                <span className="contract-tracking-action">
                  {row.meta.action}
                  <ChevronRight size={17} />
                </span>
              </button>
            );
          })
        ) : (
          <div className="compact-empty">
            Aucun dossier ne correspond à ce filtre.
          </div>
        )}
      </div>
    </section>

    {selectedContract&&<div className="drawer-backdrop" onMouseDown={(e)=>e.target===e.currentTarget&&setSelectedContractId('')}><aside className="registration-drawer"><header className="drawer-header"><div><p className="eyebrow">CONTRAT FINANCIER</p><h2>{selectedContract.studentName}</h2></div><button className="drawer-close" onClick={()=>setSelectedContractId('')}><XCircle size={20}/></button></header><div className="drawer-scroll"><section className="detail-card"><h3>Résumé</h3><dl className="detail-grid"><div><dt>Parent</dt><dd>{selectedContract.parentName}</dd></div><div><dt>Année</dt><dd>{selectedContract.schoolYear}</dd></div><div><dt>Total</dt><dd>{money(selectedContract.totalAmount)}</dd></div><div><dt>Payé</dt><dd>{money(selectedContract.paidAmount)}</dd></div><div><dt>Reste</dt><dd>{money(selectedContract.remainingAmount)}</dd></div><div><dt>Statut</dt><dd>{selectedContract.status}</dd></div></dl></section>{selectedContract.discountAmount>0&&<section className="detail-card"><h3>Remise à valider</h3><div className="discount-review-v0"><PercentCircle/><div><strong>{money(selectedContract.discountAmount)}</strong><span>{selectedContract.discountReason} — {selectedContract.discountNote||'Sans commentaire'}</span></div></div>{isGerant&&selectedContract.discountStatus==='pending'&&<div className="drawer-actions"><button className="button button-secondary" onClick={()=>approveContract(selectedContract,false)}>Refuser</button><button className="button button-primary" onClick={()=>approveContract(selectedContract,true)}><BadgeCheck size={16}/>Approuver</button></div>}</section>}{isGerant&&['discount_approved','draft'].includes(selectedContract.status)&&<button className="button button-primary full-width" onClick={()=>sendExistingContract(selectedContract)}><Send size={16}/>Envoyer au parent</button>}<section className="detail-card"><h3>Échéancier</h3><div className="installment-list-v0">{(selectedContract.installments||[]).map((item)=><div key={item.id}><span><strong>{item.label}</strong><small>{item.dueDate}</small></span><b>{money(item.amount)}</b></div>)}</div></section></div></aside></div>}
  </>;
}
